import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ChevronDown } from "lucide-react";
import { T, THtml } from "@/components/editable/T";
import { EditableImage } from "@/components/editable/EditableImage";
import { PageContainer } from "@/components/layouts/MainLayout";

interface ResultItem {
  titleKey: string;
  titleDefault: string;
  bodyKey: string;
  bodyDefault: string;
  ctaKey: string;
  ctaDefault: string;
  link: string;
  image: string;
  imageKey: string;
}

interface ResultsAccordionProps {
  page: string;
  items: ResultItem[];
  titleKey?: string;
  titleDefault?: string;
}

export function ResultsAccordion({ page, items, titleKey, titleDefault }: ResultsAccordionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();

  return (
    <section className="py-20 px-6" style={{ background: 'linear-gradient(180deg, hsl(var(--background)) 0%, hsl(263 82% 63% / 0.05) 50%, hsl(var(--background)) 100%)' }}>
      <PageContainer>
        {titleKey && titleDefault && (
          <div className="text-left mb-20">
            <h2 style={{ color: '#2A2334' }}>
              <T page={page} k={titleKey} d={titleDefault} />
            </h2>
          </div>
        )}
        <div className="grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-16 items-center">
          {/* Left: Accordion */}
          <div className="space-y-0">
            {items.map((item, index) => {
              const isActive = activeIndex === index;
              return (
                <div
                  key={index}
                  className={`transition-all duration-300 cursor-pointer ${
                    isActive
                      ? "bg-white rounded-2xl p-6 lg:p-8 shadow-sm"
                      : "px-6 lg:px-8 py-5 border-b border-black/10"
                  }`}
                  onClick={() => setActiveIndex(index)}
                >
                    <div className="flex items-start justify-between gap-4">
                    <h4
                      className={`font-semibold leading-snug ${
                        isActive ? "text-lg lg:text-xl" : "text-base lg:text-lg"
                      }`}
                      style={{ color: "#2A2334" }}
                    >
                      <T page={page} k={item.titleKey} d={item.titleDefault} />
                    </h4>
                    {!isActive && (
                      <ChevronDown className="w-5 h-5 text-muted-foreground mt-1 flex-shrink-0" />
                    )}
                  </div>

                  {isActive && (
                    <div className="mt-4 animate-fade-in">
                      <THtml
                        page={page}
                        k={item.bodyKey}
                        d={item.bodyDefault}
                        as="p"
                        className="text-sm text-muted-foreground leading-relaxed mb-4"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(item.link);
                        }}
                        className="inline-flex items-center text-sm font-semibold transition-colors hover:opacity-80"
                        style={{ color: "#9055EE" }}
                      >
                        <T page={page} k={item.ctaKey} d={item.ctaDefault} />
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right: Image */}
          <div className="hidden lg:flex justify-center">
            <div className="rounded-3xl overflow-hidden" style={{ border: '10px solid rgba(255, 255, 255, 0.7)' }}>
              <EditableImage
                page={page}
                section="results"
                imageKey={items[activeIndex]?.imageKey}
                src={items[activeIndex]?.image}
                alt={items[activeIndex]?.titleDefault || "Result illustration"}
                className="w-full max-w-md h-[450px] object-cover"
              />
            </div>
          </div>
        </div>
      </PageContainer>
    </section>
  );
}
